/**
 * Aviso prévio da homologação, vindo do servidor (E63.F1.C13/C16, ADR-0068 §2.3). // ux-neutrality:allow doc-comment
 *
 * A ADR-0068 troca o bloqueio de auto-homologação por declaração, e promete // ux-neutrality:allow doc-comment
 * avisar **antes** da decisão quem elaborou o caso. A tela sozinha não sabe // ux-neutrality:allow doc-comment
 * disso: ela só enxerga `usuario_id`, que num caso do portal é a gestora // ux-neutrality:allow doc-comment
 * cliente — o perito que assina a análise e aprova o texto ficava sem aviso // ux-neutrality:allow doc-comment
 * (F-E63.F1-07). O sinal sai das MESMAS três fontes do Service. // ux-neutrality:allow doc-comment
 *
 * O estado é EXPLÍCITO (F-E63.F1-09): "ainda não respondeu" não pode se // ux-neutrality:allow doc-comment
 * confundir com "não há coincidência", senão a decisão vence a resposta e a // ux-neutrality:allow doc-comment
 * pessoa homologa sem ter visto o aviso. Quem consome desabilita a decisão // ux-neutrality:allow doc-comment
 * enquanto `status === 'loading'` — e também quando o servidor NUNCA respondeu // ux-neutrality:allow doc-comment
 * e o fallback local não acende aviso nenhum (`doServidor === false`): sem // ux-neutrality:allow doc-comment
 * nenhuma informação não há transparência a oferecer, então o produto escolhe // ux-neutrality:allow doc-comment
 * o aviso em vez da disponibilidade e oferece `retry()` na tela. // ux-neutrality:allow doc-comment
 *
 * `fallback` é o predicado local (criador do caso): quando ELE já acende o // ux-neutrality:allow doc-comment
 * aviso, a falha da pré-checagem não trava nada — a pessoa foi avisada. // ux-neutrality:allow doc-comment
 *
 * `revalidateKey` refaz a consulta quando a AUTORIA pode ter mudado na própria // ux-neutrality:allow doc-comment
 * tela (aprovar/reabrir narrativa é uma das três fontes) — sem ele o painel // ux-neutrality:allow doc-comment
 * decidiria sobre um retrato tirado na montagem. `revalidate()` é a conferência // ux-neutrality:allow doc-comment
 * imediatamente antes do PATCH, para a janela entre o clique e o envio. // ux-neutrality:allow doc-comment
 * // ux-neutrality:allow doc-comment
 * Existir não basta: o valor precisa ser DA GERAÇÃO DE AGORA (round 5 de // ux-neutrality:allow doc-comment
 * F-E63.F1-09). Cada bump de `revalidateKey` declara que a autoria pode ter // ux-neutrality:allow doc-comment
 * mudado, então o valor conferido antes dele responde por um caso que já não // ux-neutrality:allow doc-comment
 * existe: ele continua alimentando `mesmoAutor` — um aviso já visto não some da // ux-neutrality:allow doc-comment
 * tela — mas para de contar como conferência, e a decisão sem aviso não sai // ux-neutrality:allow doc-comment
 * dele. Sem isso, um `false` vencido mais duas falhas seguidas (atualização e // ux-neutrality:allow doc-comment
 * reconferência) liberavam a homologação sem a transparência da ADR-0068 §2.3. // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '@/lib/api';
import { getCaseReviewPrecheck } from '@/lib/cases';

export type ReviewSelfHintStatus = 'loading' | 'resolved' | 'error';

export interface ReviewSelfHintResult {
  /** O que a homologação declararia agora. */
  mesmoAutor: boolean;
  /** O valor veio do SERVIDOR? `false` = é só o fallback local (nada conferido). */
  doServidor: boolean;
}

export interface ReviewSelfHint {
  status: ReviewSelfHintStatus;
  /** O que a homologação declararia agora (servidor, ou `fallback` sem resposta). */
  mesmoAutor: boolean;
  /**
   * O servidor já respondeu alguma vez para este caso? Sem isto, quem consome
   * não consegue distinguir "não há coincidência" de "não sabemos" — foi
   * exatamente a confusão que deixou decidir sem aviso (F-E63.F1-09).
   */
  doServidor: boolean;
  /** Reconsulta e devolve valor + procedência; nunca rejeita. */
  revalidate: () => Promise<ReviewSelfHintResult>;
  /** Nova tentativa explícita depois de uma falha (botão da tela). */
  retry: () => void;
}

interface HintState {
  status: ReviewSelfHintStatus;
  /** `null` = o servidor ainda não respondeu (ou falhou) — não é "false". */
  value: boolean | null;
  /**
   * Geração de autoria em que `value` foi conferido. `revalidateKey` só é
   * incrementado quando uma das fontes de autoria pode ter mudado, então valor
   * de geração vencida responde por um caso que já não existe.
   */
  geracao: number | null;
}

/** Sem `revalidateKey`, a tela tem uma geração só — e ela nunca vence. */
function geracaoDe(revalidateKey: number | undefined): number {
  return revalidateKey ?? 0;
}

export function useReviewSelfHint(params: {
  caseId: string;
  enabled: boolean;
  fallback: boolean;
  revalidateKey?: number | undefined;
}): ReviewSelfHint {
  const { caseId, enabled, fallback, revalidateKey } = params;
  const client = useApiClient();
  const geracaoAtual = geracaoDe(revalidateKey);
  const [state, setState] = useState<HintState>(() =>
    enabled
      ? { status: 'loading', value: null, geracao: null }
      : { status: 'resolved', value: null, geracao: null },
  );
  const [tentativa, setTentativa] = useState(0);

  // Refs para `revalidate` não depender de valores que mudam a cada render (o
  // callback instável reagendaria o efeito de quem o usasse como dependência).
  const stateRef = useRef(state);
  stateRef.current = state;
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;
  const caseIdRef = useRef(caseId);
  const geracaoRef = useRef(geracaoAtual);
  geracaoRef.current = geracaoAtual;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'resolved', value: null, geracao: null });
      return;
    }
    let active = true;
    // Refetch do MESMO caso preserva o último valor do servidor: uma falha na
    // revalidação não pode apagar o que já foi conferido. Caso diferente zera.
    // A GERAÇÃO do valor é preservada junto e não é reescrita aqui — é ela que
    // dirá, depois, se o que sobrou ainda responde pela autoria de agora.
    const mesmoCaso = caseIdRef.current === caseId;
    caseIdRef.current = caseId;
    const geracao = geracaoDe(revalidateKey);
    setState((prev) =>
      mesmoCaso
        ? { status: 'loading', value: prev.value, geracao: prev.geracao }
        : { status: 'loading', value: null, geracao: null },
    );
    void getCaseReviewPrecheck(client, caseId)
      .then((precheck) => {
        if (active) {
          setState({ status: 'resolved', value: precheck.mesmo_autor_producao, geracao });
        }
      })
      .catch(() => {
        if (active) setState((prev) => ({ ...prev, status: 'error' }));
      });
    return () => {
      active = false;
    };
  }, [client, caseId, enabled, revalidateKey, tentativa]);

  const revalidate = useCallback(async (): Promise<ReviewSelfHintResult> => {
    const { value: anterior, geracao: geracaoAnterior } = stateRef.current;
    if (!enabled) return { mesmoAutor: anterior ?? fallbackRef.current, doServidor: false };
    const geracao = geracaoRef.current;
    try {
      const precheck = await getCaseReviewPrecheck(client, caseId);
      setState({ status: 'resolved', value: precheck.mesmo_autor_producao, geracao });
      return { mesmoAutor: precheck.mesmo_autor_producao, doServidor: true };
    } catch {
      // Falha na conferência não vira "não há coincidência": devolve o último
      // valor do servidor, se houver. Sem nenhum, `doServidor: false` avisa quem
      // consome de que ninguém conferiu nada — e aí a decisão não sai.
      setState((prev) => ({ ...prev, status: 'error' }));
      return {
        mesmoAutor: anterior ?? fallbackRef.current,
        doServidor: anterior !== null && geracaoAnterior === geracao,
      };
    }
  }, [client, caseId, enabled]);

  const retry = useCallback(() => setTentativa((n) => n + 1), []);

  return {
    status: state.status,
    mesmoAutor: state.value ?? fallback,
    // Conferido NA GERAÇÃO DE AGORA. Valor de geração vencida segue alimentando
    // `mesmoAutor` — um aviso já visto não pode sumir da tela — mas não conta
    // como conferência, então uma decisão sem aviso não sai dele (F-E63.F1-09).
    doServidor: state.value !== null && state.geracao === geracaoAtual,
    revalidate,
    retry,
  };
}
